import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { getMe, logout, PublicUser } from "../services/auth";
import { getSubscription, SubscriptionSummary } from "../services/subscription";
import { cn } from "@/lib/utils";
import { LayoutDashboard, Key, Activity, Settings, LogOut, Receipt } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
// import { Badge } from "@/components/ui/badge";

interface NavItem {
  label: string;
  to: string;
  icon: React.ElementType;
}

const navItems: NavItem[] = [
  {
    to: "api-keys",
    label: "API Keys",
    icon: Key,
  },
  {
    to: "billing",
    label: "Billing",
    icon: Receipt,
  },
  {
    to: "usage",
    label: "Usage",
    icon: Activity,
  },
  {
    to: "finance",
    label: "Financials",
    icon: LayoutDashboard,
  },
  {
    to: "devops",
    label: "DevOps",
    icon: Settings,
  },
];

export interface ProfileOutletContext {
  user: PublicUser;
  subscription: SubscriptionSummary | null;
  refreshSubscription: () => Promise<void>;
  refreshUser: () => Promise<PublicUser>;
}

const ProfileLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<PublicUser | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    const token = localStorage.getItem("token");

    if (!token) {
      navigate("/", { replace: true, state: { from: location.pathname } });
      return;
    }

    const fetchProfile = async () => {
      setIsLoading(true);

      try {
        const [me, subscriptionSummary] = await Promise.all([getMe(), getSubscription()]);

        if (!mounted) {
          return;
        }

        setUser(me);
        setSubscription(subscriptionSummary);
        setError(null);
      } catch (err: any) {
        if (!mounted) {
          return;
        }

        if (err?.response?.status === 401) {
          logout();
          navigate("/", { replace: true });
          return;
        }

        const message =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load profile. Please try again.";
        setError(message);
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    void fetchProfile();

    return () => {
      mounted = false;
    };
  }, [navigate, location.pathname]);

  const refreshUser = useCallback(async () => {
    const me = await getMe();
    setUser(me);
    return me;
  }, []);

  const refreshSubscription = useCallback(async () => {
    try {
      const summary = await getSubscription();
      setSubscription(summary);
    } catch (err) {
      console.error("Failed to refresh subscription", err);
      throw err;
    }
  }, []);

  const planMeta = useMemo(() => {
    const label = subscription?.planLabel ?? "Start";
    const description = subscription?.planDescription ?? "Basic API Access";
    const url = subscription?.planUrl ?? "https://platform.openai.com/pricing";

    return { label, description, url };
  }, [subscription]);

  const balanceLabel = useMemo(() => {
    const formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    });

    // Assuming user.balance is stored in cents or base units that need conversion if originally RUB
    // For now, simply displaying the number as USD.
    // If conversion is needed, it should happen here.
    return formatter.format(user?.balance ?? 0);
  }, [user?.balance]);

  const handleSignOut = useCallback(() => {
    logout();
    navigate("/");
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-foreground">
        <div className="text-sm text-muted-foreground">Loading workspace...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-background text-center text-foreground">
        <div className="max-w-md space-y-3">
          <h1 className="text-2xl font-semibold tracking-tight">Unable to open profile</h1>
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={() => window.location.reload()}>Retry</Button>
          <Button variant="outline" onClick={() => navigate("/")}>
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="flex min-h-screen bg-background">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 hidden w-64 flex-col border-r bg-card md:flex">
        <div className="flex h-16 items-center px-6">
          <h1 className="text-lg font-bold tracking-tight">SigLLM Profile</h1>
        </div>

        <div className="flex-1 overflow-y-auto py-6 px-4">
          <nav className="flex flex-col gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground",
                    isActive ? "bg-accent text-accent-foreground" : "text-muted-foreground"
                  )
                }
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </NavLink>
            ))}
          </nav>

          <Separator className="my-6" />

          <div className="px-2">
            <div className="rounded-lg border bg-background p-4">
              <div className="flex items-center gap-3 mb-3">
                <Avatar className="h-8 w-8">
                  <AvatarFallback>{user.email.charAt(0).toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="overflow-hidden">
                  <p className="truncate text-sm font-medium">{user.email}</p>
                  <p className="truncate text-xs text-muted-foreground">{planMeta.label}</p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Balance</span>
                  <span className="font-medium text-foreground">{balanceLabel}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Usage</span>
                  <span className="font-medium text-foreground">
                    {subscription ? `${subscription.usage} / ${subscription.limit}` : "—"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 border-t">
          <Button variant="ghost" className="w-full justify-start gap-3" onClick={handleSignOut}>
            <LogOut className="h-4 w-4" />
            Log Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 md:pl-64">
        <div className="container py-10 px-8 max-w-7xl mx-auto">
          <Outlet
            context={{
              user,
              subscription,
              refreshSubscription,
              refreshUser,
            }}
          />
        </div>
      </main>
    </div>
  );
};

export default ProfileLayout;
